import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { isPostHogConfigured } from "@/lib/posthogAnalytics";
import { isPostHogDisabledPath } from "@/lib/posthogPathGuard";
import {
  getActiveSecondsOnPage,
  getPageEngagementContext,
  incrementPageClickCount,
  markSectionViewed,
  resetPageEngagementState,
  setEngagementTemplate,
  updateMaxScrollPct,
} from "@/lib/posthogEngagementContext";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const SECTION_SELECTORS = [
  "[data-ph-section]",
  "section[id]",
  "main [id]",
  "header[id]",
  "footer[id]",
].join(",");

const CLICK_SELECTORS =
  "button, a, [data-ph-capture], [data-attr], [role='button']";

const HEARTBEAT_MS = 30_000;
const SECTION_THRESHOLD = 0.35;

function sectionIdFromElement(el: Element): string | null {
  return (
    el.getAttribute("data-ph-section") ||
    el.getAttribute("data-section") ||
    (el.id ? el.id : null)
  );
}

/**
 * Deep engagement: section visibility, enriched clicks, active-time pulses, page summary.
 */
export function PostHogEngagementTracker() {
  const location = useLocation();
  const { templateName } = useCurrentTemplate();
  const disabled = isPostHogDisabledPath(location.pathname);
  const pulseCount = useRef(0);

  useEffect(() => {
    if (disabled) return;
    resetPageEngagementState(location.pathname);
    setEngagementTemplate(templateName ?? null);
    pulseCount.current = 0;
  }, [location.pathname, templateName, disabled]);

  // ── Section visibility (what they see) ───────────────────────────────────
  useEffect(() => {
    if (disabled || typeof window === "undefined" || !window.IntersectionObserver) {
      return;
    }

    const seen = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        if (!isPostHogConfigured()) return;
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < SECTION_THRESHOLD) {
            continue;
          }
          const id = sectionIdFromElement(entry.target);
          if (!id || seen.has(id)) continue;
          seen.add(id);
          markSectionViewed(id);

          const rect = entry.boundingClientRect;
          window.posthog?.capture("section_viewed", {
            section_id: id,
            section_label:
              entry.target.getAttribute("data-ph-section-label") || id,
            visible_pct: Math.round(entry.intersectionRatio * 100),
            viewport_y: Math.round(rect.top),
            ...getPageEngagementContext(),
          });
        }
      },
      { threshold: [SECTION_THRESHOLD, 0.5, 0.75] },
    );

    const observeAll = () => {
      document.querySelectorAll(SECTION_SELECTORS).forEach((el) => {
        if (sectionIdFromElement(el)) observer.observe(el);
      });
    };

    observeAll();
    const mo = new MutationObserver(() => observeAll());
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      observer.disconnect();
    };
  }, [location.pathname, disabled]);

  // ── Enriched element clicks ──────────────────────────────────────────────
  useEffect(() => {
    if (disabled) return;

    const onClick = (ev: MouseEvent) => {
      if (!isPostHogConfigured()) return;
      const target = ev.target as Element | null;
      if (!target?.closest) return;
      const el = target.closest(CLICK_SELECTORS);
      if (!el) return;

      incrementPageClickCount();
      const label =
        el.getAttribute("data-ph-capture") ||
        el.getAttribute("data-attr") ||
        el.getAttribute("aria-label") ||
        (el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 120);

      window.posthog?.capture("element_click", {
        element_tag: el.tagName.toLowerCase(),
        element_id: el.id || null,
        element_label: label || null,
        element_href: (el as HTMLAnchorElement).href || null,
        section_id: (() => {
          let node: Element | null = el;
          while (node) {
            const sid = sectionIdFromElement(node);
            if (sid) return sid;
            node = node.parentElement;
          }
          return null;
        })(),
        click_x: Math.round(ev.clientX),
        click_y: Math.round(ev.clientY),
        ...getPageEngagementContext(),
      });
    };

    document.addEventListener("click", onClick, { capture: true, passive: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, [location.pathname, disabled]);

  // ── Scroll max (feeds context on every event) ────────────────────────────
  useEffect(() => {
    if (disabled) return;
    let raf: number | null = null;

    const check = () => {
      raf = null;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.round(
        ((window.scrollY || document.documentElement.scrollTop) / docHeight) *
          100,
      );
      updateMaxScrollPct(pct);
    };

    const onScroll = () => {
      if (raf === null) raf = requestAnimationFrame(check);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [location.pathname, disabled]);

  // ── Active-time pulse + page summary on leave ────────────────────────────
  useEffect(() => {
    if (disabled) return;

    const interval = window.setInterval(() => {
      if (
        !isPostHogConfigured() ||
        document.visibilityState !== "visible"
      ) {
        return;
      }
      pulseCount.current += 1;
      window.posthog?.capture("engagement_pulse", {
        pulse_index: pulseCount.current,
        ...getPageEngagementContext(),
      });
    }, HEARTBEAT_MS);

    return () => {
      clearInterval(interval);
      if (!isPostHogConfigured()) return;
      const seconds = getActiveSecondsOnPage();
      if (seconds < 2) return;
      window.posthog?.capture("page_engagement_summary", {
        ...getPageEngagementContext(),
        total_pulses: pulseCount.current,
      });
    };
  }, [location.pathname, disabled]);

  return null;
}